import {
  check2FAResetTokenMutationDocument,
  checkPasswordResetTokenMutationDocument,
  discoverLoginMethodMutationDocument,
  loginMutationDocument,
  refreshAccessTokenMutationDocument,
  request2FAResetLinkMutationDocument,
  requestPasswordResetLinkMutationDocument,
  reset2FAWithTokenMutationDocument,
  updatePasswordWithTokenMutationDocument,
} from "@/domain/_shared/auth/hooks/api/auth.gql";
import { APIError, createAPIError } from "@/services/errors.service";
import { posthogService } from "@/services/posthog.service";
import { API_ENDPOINT, createOperationClient } from "@/services/react-query.service/graphql-client";
import { getAccessToken, removeAccessToken, setAccessToken } from "@/services/storage.service";
import { removeWorkspaceSettings } from "@/services/workspace.service";

import type { FederatedLoginHandoff } from "./federated-login-handoff";
import { isFederatedLoginHandoff } from "./is-federated-login-handoff";
import { parseLoginResponse } from "./parse-login-response";

export { API_ENDPOINT };

export const ACCESS_TOKEN_KEY = "ACCESS_TOKEN";

export const SETUP_TOKEN_KEY = "2fa_setup_token";

export const getSetupToken = () => sessionStorage.getItem(SETUP_TOKEN_KEY);
export const setSetupToken = (token: string) => sessionStorage.setItem(SETUP_TOKEN_KEY, token);
export const removeSetupToken = () => sessionStorage.removeItem(SETUP_TOKEN_KEY);

export const createSetupTokenClient = async (operationName: string) => {
  const setupToken = getSetupToken();
  if (!setupToken) {
    throw new Error("Setup token not found");
  }
  const { GraphQLClient } = await import("graphql-request");
  return new GraphQLClient(`${API_ENDPOINT}/graphql/${operationName}`, {
    requestMiddleware: (request) => {
      request.headers = {
        ...request.headers,
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${setupToken}`,
      };
      return request;
    },
  });
};

export const isAuthenticated = () => {
  const accessToken = getAccessToken();

  if (!accessToken) {
    return false;
  }
  return true;
};

export const signout = () => {
  removeWorkspaceSettings();
  removeAccessToken();
  removeSetupToken();
  posthogService.reset();
  window.location.replace(new URL(`${window.location.origin}/`));
};

export interface LoginResponse {
  token?: string;
  setupRequired?: boolean;
  requires2FA?: boolean;
  twoFactorToken?: string;
  status?: string;
  tempToken?: string;
  message?: string;
}

export const login = async (email: string, password: string): Promise<LoginResponse | FederatedLoginHandoff> => {
  const client = createOperationClient("login");
  const data = await client.request(loginMutationDocument, {
    input: {
      email: email,
      password: password,
    },
  });

  if (!data || !data.login) {
    throw createAPIError({
      statusCode: 401,
      message: "The server did not respond with a valid response",
    });
  }

  if (isFederatedLoginHandoff(data.login)) {
    return { requiresFederatedLogin: true, startUrl: data.login.startUrl };
  }

  return applyPostPrimaryOutcome(data.login);
};

const applyPostPrimaryOutcome = (loginResponse: LoginResponse): LoginResponse => {
  if (loginResponse.requires2FA && loginResponse.twoFactorToken) {
    return {
      requires2FA: true,
      twoFactorToken: loginResponse.twoFactorToken,
      message: loginResponse.message,
    };
  }

  if (loginResponse.status === "SETUP_REQUIRED" && loginResponse.tempToken) {
    removeAccessToken();
    setSetupToken(loginResponse.tempToken);
    return {
      setupRequired: true,
      status: loginResponse.status,
      message: loginResponse.message,
    };
  }

  if (loginResponse.token) {
    removeSetupToken();
    setAccessToken(loginResponse.token);
    return {
      token: loginResponse.token,
    };
  }

  throw createAPIError({
    statusCode: 401,
    message: "The server did not respond with an access token",
  });
};

export const discoverLoginMethod = async (email: string) => {
  const client = createOperationClient("discoverLoginMethod");
  const data = await client.request(discoverLoginMethodMutationDocument, { input: { email } });

  return data.discoverLoginMethod;
};

export const startFederatedLogin = (startUrl: string) => {
  window.location.assign(startUrl);
};

export const exchangeFederatedLoginCode = async (code: string): Promise<LoginResponse> => {
  const response = await fetch(`${API_ENDPOINT}/auth/sso/exchange`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });

  if (!response.ok) {
    throw createAPIError({
      statusCode: response.status,
      message: "The login could not be completed",
    });
  }

  return applyPostPrimaryOutcome(parseLoginResponse(await response.json()));
};

export const verify2FAAndLogin = async (twoFactorToken: string, code: string) => {
  const { GraphQLClient } = await import("graphql-request");
  const { verify2FACodeMutationDocument } = await import("@/domain/two-factor-auth/hooks/api/two-factor-auth.gql");

  const client = new GraphQLClient(`${API_ENDPOINT}/graphql/verify2FACode`, {
    requestMiddleware: (request) => {
      request.headers = {
        ...request.headers,
        "Content-Type": "application/json",
        Accept: "application/json",
        Authorization: `Bearer ${twoFactorToken}`,
      };
      return request;
    },
  });

  try {
    const data = await client.request(verify2FACodeMutationDocument, {
      input: { code },
    });

    if (!data || !data.verify2FACode) {
      throw createAPIError({
        statusCode: 401,
        message: "Invalid code, please try another",
      });
    }

    removeSetupToken();
    setAccessToken(data.verify2FACode.token);
  } catch (error: unknown) {
    const graphqlError = error as {
      response?: { errors?: Array<{ message?: string }>; status?: number };
    };
    if (graphqlError.response?.errors?.[0]?.message) {
      const message = graphqlError.response.errors[0].message;
      if (message.includes("Invalid verification code") || message.includes("verification code")) {
        throw createAPIError({
          statusCode: 401,
          message: "Invalid code, please try another",
        });
      }
      throw createAPIError({
        statusCode: graphqlError.response.status || 401,
        message: message,
      });
    }
    if (error instanceof APIError) {
      throw error;
    }
    throw createAPIError({
      statusCode: 401,
      message: "Invalid code, please try another",
    });
  }
};

export const requestPasswordResetLink = async (email: string) => {
  const client = createOperationClient("requestPasswordResetLink");
  const data = await client.request(requestPasswordResetLinkMutationDocument, {
    input: {
      email: email,
    },
  });

  if (!data) {
    throw createAPIError({
      statusCode: 401,
      message: "The server did not respond with an access token",
    });
  }
};

type PasswordResetTokenMetadatResponseType = {
  success: boolean;
  email: string;
  message: string;
};

export const isValidPasswordResetToken = async (token: string) => {
  const client = createOperationClient("checkPasswordResetToken");
  const data = await client.request(checkPasswordResetTokenMutationDocument, {
    input: {
      token: token,
    },
  });

  if (!data) {
    throw createAPIError({
      statusCode: 401,
      message: "The server did not respond with an access token",
    });
  }

  return {
    ...data.checkPasswordResetToken,
  } as PasswordResetTokenMetadatResponseType;
};

export const resetPasswordWithToken = async (token: string, password: string) => {
  const client = createOperationClient("updatePasswordWithToken");
  const data = await client.request(updatePasswordWithTokenMutationDocument, {
    input: {
      token: token,
      password: password,
    },
  });

  if (!data) {
    throw createAPIError({
      statusCode: 401,
      message: "The server did not respond with an access token",
    });
  }
};

export const refreshAccessToken = async () => {
  const client = createOperationClient("refreshAccessToken");
  const data = await client.request(refreshAccessTokenMutationDocument);

  if (!data) {
    throw createAPIError({
      statusCode: 401,
      message: "The server did not respond with an access token",
    });
  }

  setAccessToken(data.refresh.token);
};

export const request2FAResetLink = async (email: string) => {
  const client = createOperationClient("request2FAResetLink");
  const data = await client.request(request2FAResetLinkMutationDocument, {
    input: {
      email: email,
    },
  });

  if (!data) {
    throw createAPIError({
      statusCode: 401,
      message: "The server did not respond with a valid response",
    });
  }
};

type TwoFactorAuthResetTokenMetadataResponseType = {
  success: boolean;
  email: string;
  message: string;
};

export const isValid2FAResetToken = async (token: string) => {
  const client = createOperationClient("check2FAResetToken");
  const data = await client.request(check2FAResetTokenMutationDocument, {
    input: {
      token: token,
    },
  });

  if (!data) {
    throw createAPIError({
      statusCode: 401,
      message: "The server did not respond with a valid response",
    });
  }

  return {
    ...data.check2FAResetToken,
  } as TwoFactorAuthResetTokenMetadataResponseType;
};

export const reset2FAWithToken = async (token: string) => {
  const client = createOperationClient("reset2FAWithToken");
  const data = await client.request(reset2FAWithTokenMutationDocument, {
    input: {
      token: token,
    },
  });

  if (!data) {
    throw createAPIError({
      statusCode: 401,
      message: "The server did not respond with a valid response",
    });
  }
};
